import React, { FC, useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { debounce } from 'lodash';
import { searchMerchants } from 'helpers/api';
import { MerchantSearchResult } from 'reduxState/store/performanceSummary/types';
import { isMerchantSearchResultsList } from 'pages/PerformanceSummary/helpers';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

function getOptionLabel(option: MerchantSearchResult): string {
  return `${option.Value} (${option.ID})`;
}

export interface MerchantSearchFieldProps {
  value: string;
  onChange: (merchantId: string) => void;
  /** Called when user selects a merchant from the dropdown (provides display name for title generation). */
  onMerchantSelect?: (merchantId: string, merchantName: string) => void;
  label?: string;
  placeholder?: string;
  hasError?: boolean;
  errorText?: string;
  isDisabled?: boolean;
}

/**
 * Type-ahead merchant search field. Users can search by merchant name (substring)
 * and select a result to set the merchant ID, or type/paste a numeric ID directly.
 */
const MerchantSearchField: FC<MerchantSearchFieldProps> = ({
  value,
  onChange,
  onMerchantSelect,
  label = 'Merchant ID',
  placeholder = 'Search by merchant name or enter Merchant ID',
  hasError = false,
  errorText,
  isDisabled = false,
}) => {
  const [options, setOptions] = useState<MerchantSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<MerchantSearchResult | null>(null);
  const lastSyncedValueRef = useRef<string>(value);

  // Sync display when value is set externally (e.g. form load, duplicate campaign, form reset)
  useEffect(() => {
    if (value === lastSyncedValueRef.current) return;
    lastSyncedValueRef.current = value;
    if (!value) {
      setSelectedOption(null);
      setInputValue('');
    } else {
      setSelectedOption(null);
      setInputValue(value);
    }
  }, [value]);

  const searchMerchantsDebounced = React.useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < MIN_SEARCH_LENGTH) {
          setOptions([]);
          return;
        }
        setLoading(true);
        const results = await searchMerchants(query);
        setLoading(false);
        if (isMerchantSearchResultsList(results)) {
          const merchantResults = results.filter(r => r.Type === 'merchant' && !r.Disabled);
          setOptions(merchantResults);
        } else {
          setOptions([]);
        }
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    return () => {
      searchMerchantsDebounced.cancel();
    };
  }, [searchMerchantsDebounced]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    searchMerchantsDebounced(newInputValue);
    // If user clears the input, clear the value
    if (!newInputValue.trim()) {
      lastSyncedValueRef.current = '';
      onChange('');
      setSelectedOption(null);
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: MerchantSearchResult | string | null) => {
    if (newValue === null) {
      setSelectedOption(null);
      setInputValue('');
      lastSyncedValueRef.current = '';
      onChange('');
      return;
    }
    if (typeof newValue === 'string') {
      // freeSolo: user typed a value - use it if it's a number
      const trimmed = newValue.trim();
      if (/^\d+$/.test(trimmed)) {
        lastSyncedValueRef.current = trimmed;
        onChange(trimmed);
        setSelectedOption(null);
        setInputValue(trimmed);
      }
      return;
    }
    lastSyncedValueRef.current = String(newValue.ID);
    setSelectedOption(newValue);
    setInputValue(getOptionLabel(newValue));
    onChange(String(newValue.ID));
    onMerchantSelect?.(String(newValue.ID), newValue.Value);
  };

  const handleBlur = () => {
    // When blurring, if input looks like a numeric ID and we don't have a selection, use it
    const trimmed = inputValue.trim();
    if (trimmed && /^\d+$/.test(trimmed) && !selectedOption) {
      onChange(trimmed);
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={handleBlur}
      getOptionLabel={option => (typeof option === 'string' ? option : getOptionLabel(option))}
      filterOptions={x => x}
      isOptionEqualToValue={(option, val) => option.ID === val.ID}
      disabled={isDisabled}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={hasError}
          helperText={errorText}
          inputProps={{
            ...params.inputProps,
            'aria-label': label,
            'data-testid': 'merchant-search-input',
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.ID}>
          {getOptionLabel(option)}
        </li>
      )}
    />
  );
};

export default MerchantSearchField;
